import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const PortfolioChart = ({ data, type = 'pie', title }) => {
  const COLORS = [
    'var(--color-primary)',
    'var(--color-secondary)', 
    'var(--color-accent)',
    'var(--color-success)',
    'var(--color-warning)',
    'var(--color-error)'
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-subtle">
          <p className="text-sm font-medium text-popover-foreground">
            {label || payload?.[0]?.name}
          </p>
          <p className="text-sm text-muted-foreground">
            {`${payload?.[0]?.value?.toLocaleString()} tCO2e`}
          </p>
          {payload?.[0]?.payload?.percentage && (
            <p className="text-xs text-muted-foreground">
              {`${payload?.[0]?.payload?.percentage}% of portfolio`}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const renderPieChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percentage }) => `${name} (${percentage}%)`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {data?.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis 
          dataKey="name" 
          stroke="var(--color-muted-foreground)"
          fontSize={12}
        />
        <YAxis 
          stroke="var(--color-muted-foreground)"
          fontSize={12}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="value" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-card-foreground">{title}</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {}}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-smooth ${
              type === 'pie' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Pie
          </button>
          <button
            onClick={() => {}}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-smooth ${
              type === 'bar' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Bar
          </button>
        </div>
      </div>
      {type === 'pie' ? renderPieChart() : renderBarChart()}
      {/* Legend */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
        {data?.map((item, index) => (
          <div key={item?.name} className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
            />
            <span className="text-xs text-muted-foreground truncate">
              {item?.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PortfolioChart;